/**
 * Subscription Service
 * 
 * API service for subscription, billing, and plan management
 */

import apiClient from './api';
import { calculateRemainingDays, calculateTotalDays } from '../utils/subscriptionDates';
import type {
  Plan,
  Subscription,
  PlansResponse,
  SubscriptionResponse,
  StartTrialRequest,
  StartTrialResponse,
  UpgradePlanRequest,
  UpgradePlanResponse,
  ApplyPromoResponse,
  UsageResponse,
  InvoicesResponse,
  CancelSubscriptionRequest,
  ReferralResponse,
  BillingCycle,
  PlanTier,
} from '../types/subscription';

class SubscriptionService {
  private basePath = '/subscription';

  // ========================
  // PLAN ENDPOINTS
  // ========================

  /**
   * Get all available plans
   */
  async getPlans(): Promise<Plan[]> {
    const response = await apiClient.get<PlansResponse>(`${this.basePath}/plans`);
    return response.data.data.plans;
  }

  /**
   * Get a specific plan by ID
   */
  async getPlan(planId: PlanTier): Promise<Plan> {
    const response = await apiClient.get<{ success: boolean; data: { plan: Plan } }>(
      `${this.basePath}/plans/${planId}`
    );
    return response.data.data.plan;
  }

  /**
   * Compare two plans
   */
  async comparePlans(currentPlanId: PlanTier, newPlanId: PlanTier): Promise<{
    isUpgrade: boolean;
    isDowngrade: boolean;
    priceDifference: number;
    featuresDiff: { gained: string[]; lost: string[] };
  }> {
    const response = await apiClient.get(
      `${this.basePath}/plans/compare?current=${currentPlanId}&new=${newPlanId}`
    );
    return response.data.data;
  }

  // ========================
  // SUBSCRIPTION ENDPOINTS
  // ========================

  /**
   * Get current subscription status
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse['data']> {
    const response = await apiClient.get<SubscriptionResponse>(`${this.basePath}/current`);
    return response.data.data;
  }

  /**
   * Start a free trial using the selected plan's configured duration
   */
  async startTrial(options?: StartTrialRequest): Promise<StartTrialResponse['data']> {
    const response = await apiClient.post<StartTrialResponse>(
      `${this.basePath}/trial/start`,
      options || {}
    );
    return response.data.data;
  }

  /**
   * Check trial eligibility
   */
  async checkTrialEligibility(): Promise<{
    eligible: boolean;
    reason?: string;
    defaultPlan: PlanTier;
    trialDays: number;
  }> {
    const response = await apiClient.get(`${this.basePath}/trial/eligibility`);
    return response.data.data;
  }

  /**
   * Subscribe to a plan (new subscription or convert from trial)
   */
  async subscribe(options: {
    planSlug: PlanTier;
    billingCycle?: BillingCycle;
    paymentInfo?: {
      paymentMethod?: string;
      transactionId?: string;
      billingInfo?: {
        companyName?: string;
        email?: string;
        address?: Record<string, string>;
      };
    };
  }): Promise<{
    subscription: Subscription;
    invoice?: {
      id: string;
      invoiceRef: string;
      total: number;
      dueDate: string;
    };
  }> {
    const response = await apiClient.post(`${this.basePath}/subscribe`, options);
    return response.data.data;
  }

  /**
   * Upgrade to a new plan (or downgrade/change - handles all plan changes)
   */
  async upgradePlan(options: UpgradePlanRequest): Promise<UpgradePlanResponse['data'] & { message?: string }> {
    const response = await apiClient.post<UpgradePlanResponse>(
      `${this.basePath}/upgrade`,
      options
    );
    return {
      ...response.data.data,
      message: response.data.message // Include backend message for changeType-aware messaging
    };
  }

  /**
   * Downgrade to a lower plan (effective at end of billing period)
   */
  async downgradePlan(planId: PlanTier): Promise<{
    subscription: Subscription;
    effectiveDate: string;
  }> {
    const response = await apiClient.post(`${this.basePath}/downgrade`, { planId });
    return response.data.data;
  }

  /**
   * Change billing cycle (monthly <-> yearly)
   */
  async changeBillingCycle(billingCycle: BillingCycle): Promise<{
    subscription: Subscription;
    newPrice: number;
    savings?: number;
  }> {
    const response = await apiClient.patch(`${this.basePath}/billing-cycle`, { billingCycle });
    return response.data.data;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(options: CancelSubscriptionRequest): Promise<{
    subscription: Subscription;
    canceledAt: string;
    accessUntil: string;
  }> {
    const response = await apiClient.post(`${this.basePath}/cancel`, options);
    return response.data.data;
  }

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{
    subscription: Subscription;
    message: string;
  }> {
    const response = await apiClient.post(`${this.basePath}/reactivate`);
    return response.data.data;
  }

  /**
   * Pause subscription (if plan allows)
   */
  async pauseSubscription(days: number): Promise<{
    subscription: Subscription;
    pausedUntil: string;
  }> {
    const response = await apiClient.post(`${this.basePath}/pause`, { days });
    return response.data.data;
  }

  /**
   * Resume a paused subscription
   */
  async resumeSubscription(): Promise<{
    subscription: Subscription;
  }> {
    const response = await apiClient.post(`${this.basePath}/resume`);
    return response.data.data;
  }

  // ========================
  // USAGE & LIMITS
  // ========================

  /**
   * Get current usage and limits
   */
  async getUsage(): Promise<UsageResponse['data']> {
    const response = await apiClient.get<UsageResponse>(`${this.basePath}/usage`);
    return response.data.data;
  }

  /**
   * Check if a specific feature is available
   */
  async checkFeature(featureKey: string): Promise<{
    available: boolean;
    limit?: number;
    used?: number;
    upgradeRequired?: PlanTier;
  }> {
    const response = await apiClient.get(`${this.basePath}/features/${featureKey}`);
    return response.data.data;
  }

  // ========================
  // BILLING & INVOICES
  // ========================

  /**
   * Get invoice history
   */
  async getInvoices(page = 1, limit = 10): Promise<InvoicesResponse['data']> {
    const response = await apiClient.get<InvoicesResponse>(
      `${this.basePath}/invoices?page=${page}&limit=${limit}`
    );
    return response.data.data;
  }

  /**
   * Get a specific invoice
   */
  async getInvoice(invoiceId: string): Promise<{ invoice: any; downloadUrl: string }> {
    const response = await apiClient.get(`${this.basePath}/invoices/${invoiceId}`);
    return response.data.data;
  }

  /**
   * Download invoice as PDF
   */
  async downloadInvoice(invoiceId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.basePath}/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Get upcoming invoice preview
   */
  async getUpcomingInvoice(): Promise<{
    amount: number;
    dueDate: string;
    lineItems: { description: string; amount: number }[];
    discounts: { description: string; amount: number }[];
  }> {
    const response = await apiClient.get(`${this.basePath}/invoices/upcoming`);
    return response.data.data;
  }

  // ========================
  // PROMOTIONS & DISCOUNTS
  // ========================

  /**
   * Apply a promotion code
   */
  async applyPromoCode(promoCode: string): Promise<ApplyPromoResponse['data']> {
    const response = await apiClient.post<ApplyPromoResponse>(`${this.basePath}/promo/apply`, {
      promoCode,
    });
    return response.data.data;
  }

  /**
   * Validate a promotion code (without applying)
   */
  async validatePromoCode(promoCode: string): Promise<{
    valid: boolean;
    discount?: number;
    discountType?: 'percent' | 'amount';
    description?: string;
    expiresAt?: string;
  }> {
    const response = await apiClient.get(
      `${this.basePath}/promo/validate?code=${encodeURIComponent(promoCode)}`
    );
    return response.data.data;
  }

  /**
   * Remove applied promotion
   */
  async removePromoCode(): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`${this.basePath}/promo`);
    return response.data;
  }

  // ========================
  // REFERRALS
  // ========================

  /**
   * Get referral information
   */
  async getReferralInfo(): Promise<ReferralResponse['data']> {
    const response = await apiClient.get<ReferralResponse>(`${this.basePath}/referral`);
    return response.data.data;
  }

  /**
   * Generate new referral code
   */
  async regenerateReferralCode(): Promise<{ referralCode: string; referralLink: string }> {
    const response = await apiClient.post(`${this.basePath}/referral/regenerate`);
    return response.data.data;
  }

  /**
   * Apply referral code (when signing up)
   */
  async applyReferralCode(code: string): Promise<{
    success: boolean;
    bonus: string;
  }> {
    const response = await apiClient.post(`${this.basePath}/referral/apply`, { code });
    return response.data.data;
  }

  /**
   * Validate a referral code
   */
  async validateReferralCode(code: string): Promise<{
    success: boolean;
    message?: string;
    data?: {
      referrer?: {
        name: string;
      };
      benefit?: string;
    };
  }> {
    const response = await apiClient.post(`${this.basePath}/referral/validate`, { code });
    return response.data;
  }

  // ========================
  // HELPERS
  // ========================

  /**
   * Format price for display
   */
  formatPrice(amount: number, currency = 'TND'): string {
    return `${amount} ${currency}`;
  }

  /**
   * Format limit for display
   */
  formatLimit(value: number): string {
    return value === -1 ? 'Unlimited' : value.toString();
  }

  /**
   * Calculate trial days remaining from end date
   * Properly handles time zones and rounding for custom trial periods
   */
  calculateTrialDaysRemaining(trialEndDate: string): number {
    return calculateRemainingDays(trialEndDate);
  }

  /**
   * Calculate total trial duration in days
   */
  calculateTrialTotalDays(trialStartDate: string, trialEndDate: string): number {
    return calculateTotalDays(trialStartDate, trialEndDate) || 30;
  }

  /**
   * Get plan color by tier
   */
  getPlanColor(planId: PlanTier): string {
    const colors: Record<PlanTier, string> = {
      basic: '#6B7280',
      pro: '#273B7D',
      elite: '#D4AF37',
      flash: '#F97316',
    };
    return colors[planId] || '#273B7D';
  }

  /**
   * Get plan icon by tier
   */
  getPlanIcon(planId: PlanTier): string {
    const icons: Record<PlanTier, string> = {
      basic: 'DirectionsCar',
      pro: 'Rocket',
      elite: 'Crown',
      flash: 'Bolt',
    };
    return icons[planId] || 'Star';
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
